import React, { useContext, useEffect, useMemo, useState } from "react"
import propTypes from "prop-types"
import { useParams, Link } from "react-router-dom"
import { Alert, Col, Container, Pagination, PaginationItem, PaginationLink, Row, Table } from "reactstrap"
import { useQuery } from "@tanstack/react-query"

import { Loading } from "components"
import { getLog } from "services/logs-importation"
import { UserContext } from "App"
import { ItemListRow } from "components/logs-importation"

const ITEMS_PER_PAGE = 12

const extractItemName = (item) => {
  if (item?.name) {
    return String(item.name).trim()
  }

  const message = String(item?.message ?? "")
  const match = message.match(/-([^-]+)-/)
  return match?.[1]?.trim() ?? ""
}

const LogSectionTable = (props) => {
  const { title, items, styleCenterTable, defaultOpen = false } = props
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false)
  const [currentPage, setCurrentPage] = useState(1)
  const [messageFilter, setMessageFilter] = useState("")

  const safeItems = useMemo(() => items ?? [], [items])
  const filteredItems = useMemo(() => {
    const normalizedFilter = messageFilter.trim().toLowerCase()

    if (!normalizedFilter) {
      return safeItems
    }

    return safeItems.filter((item) => {
      const itemName = extractItemName(item).toLowerCase()
      return itemName.includes(normalizedFilter)
    })
  }, [messageFilter, safeItems])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE))

  useEffect(() => {
    setCurrentPage(1)
  }, [safeItems.length, messageFilter])

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    const end = start + ITEMS_PER_PAGE
    return filteredItems.slice(start, end)
  }, [currentPage, filteredItems])

  const visiblePages = useMemo(() => {
    const pages = []
    const start = Math.max(1, currentPage - 2)
    const end = Math.min(totalPages, currentPage + 2)

    for (let page = start; page <= end; page += 1) {
      pages.push(page)
    }

    return pages
  }, [currentPage, totalPages])

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) {
      return
    }

    setCurrentPage(page)
  }

  return (
    <div className="border rounded mb-3">
      <button
        type="button"
        className="btn btn-light w-100 d-flex justify-content-between align-items-center"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="fw-semibold">
          {title} ({safeItems.length})
        </span>
        <i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} />
      </button>

      {isOpen && (
        <div className="p-3">
          <div className="row g-2 align-items-end mb-3">
            <div className="col-12 col-md-7" style={{ width: "30%" }}>
              <label htmlFor={`filter-${title}`} className="form-label mb-1">
                Buscar por nombre
              </label>
              <input
                id={`filter-${title}`}
                type="text"
                className="form-control"
                placeholder="Producto o presentación..."
                value={messageFilter}
                onChange={(event) => setMessageFilter(event.target.value)}
              />
            </div>
          </div>

          <Table hover responsive>
            <thead>
              <tr className="table-secondary">
                <th style={styleCenterTable}>Renglon</th>
                <th style={{ textAlign: "left", verticalAlign: "middle" }}>Nombre</th>
                <th style={{ textAlign: "left", verticalAlign: "middle" }}>Mensaje</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.length > 0 ? (
                paginatedItems.map((item, index) => {
                  const key = item?.id ?? `${item?.row ?? "row"}-${index}`
                  return <ItemListRow key={key} item={item} displayName={extractItemName(item)} />
                })
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">
                    {messageFilter ? "No hay coincidencias" : "No hay datos"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {filteredItems.length > ITEMS_PER_PAGE && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
              <small className="text-muted">
                Pagina {currentPage} de {totalPages}
              </small>
              <Pagination
                aria-label={`Paginacion de ${title}`}
                className="mb-0 d-flex align-items-center gap-1 flex-wrap"
              >
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                </PaginationItem>

                {visiblePages.map((page) => (
                  <PaginationItem key={page} active={page === currentPage}>
                    <PaginationLink onClick={() => handlePageChange(page)}>{page}</PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem disabled={currentPage === totalPages}>
                  <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

LogSectionTable.propTypes = {
  title: propTypes.string.isRequired,
  items: propTypes.array,
  styleCenterTable: propTypes.object,
  defaultOpen: propTypes.bool,
}

const LogsImportationRetrieve = () => {
  document.title = "Log de importación | Punto de venta"

  const { id } = useParams()
  const { data, error, isLoading, isError, refetch } = useQuery(["logs-imporation", id], () => getLog(id))
  const userData = useContext(UserContext)
  const isAdmin = userData?.user_type

  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }

  const statusConfig = {
    pending: { label: "En cola", className: "bg-secondary" },
    processing: { label: "Procesando", className: "bg-warning text-dark" },
    success: { label: "Completado", className: "bg-success" },
    failed: { label: "Fallido", className: "bg-danger" },
  }

  const currentStatus = statusConfig[data?.status] ?? statusConfig.success
  const isInProgress = data?.status === "pending" || data?.status === "processing"

  useEffect(() => {
    if (!isInProgress) {
      return undefined
    }

    const interval = window.setInterval(() => {
      refetch()
    }, 3000)

    return () => window.clearInterval(interval)
  }, [isInProgress, refetch])

  const isErrorDiv = () => {
    return (
      <div className="text-center">
        <Alert color="danger" isOpen={isError}>
          {error?.errors?.map((item) => item?.message)}
        </Alert>
      </div>
    )
  }

  const renderStatusBadge = () => (
    <span className={`badge ${currentStatus.className}`} style={{ marginLeft: "10px" }}>
      {currentStatus.label}
    </span>
  )

  const renderCompletedContent = () => (
    <>
      <h1 style={{ textAlign: "center" }}>{data?.type_model}</h1>
      {data?.status === "failed" && (
        <Alert color="danger">
          {data?.error_message || data?.log_file?.error_message || "La importación falló antes de generar el reporte."}
        </Alert>
      )}

      <LogSectionTable
        title="Registros creados"
        items={data?.log_file?.created}
        styleCenterTable={styleCenterTable}
        defaultOpen
      />
      <LogSectionTable
        title="Registros actualizados"
        items={data?.log_file?.updated}
        styleCenterTable={styleCenterTable}
      />
      <LogSectionTable
        title="Registros con errores"
        items={data?.log_file?.errors}
        styleCenterTable={styleCenterTable}
      />

      {data?.type_model === "Productos" && (
        <>
          <h1 className="mt-3" style={{ textAlign: "center" }}>
            Presentaciones
          </h1>
          <LogSectionTable
            title="Registros creados"
            items={data?.log_file?.created_presentations}
            styleCenterTable={styleCenterTable}
            defaultOpen
          />
          <LogSectionTable
            title="Registros actualizados"
            items={data?.log_file?.updated_presentations}
            styleCenterTable={styleCenterTable}
          />
          <LogSectionTable
            title="Registros con errores"
            items={data?.log_file?.errors_presentations}
            styleCenterTable={styleCenterTable}
          />
        </>
      )}
    </>
  )

  return !isAdmin ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <div className="page-content">
      <Container fluid>
        <Row style={{ marginBottom: "20px" }}>
          <Col className="text-start">
            <div style={{ display: "flex", alignItems: "center" }}>
              <Link to="/logs-importation" className="btn btn-primary" style={{ marginRight: "10px" }}>
                <i className="fa fa-arrow-left" /> Volver
              </Link>
              <h1 style={{ marginBottom: "0" }}>
                Log de importación: {data?.id}
                {data?.status ? renderStatusBadge() : null}
              </h1>
            </div>
          </Col>
        </Row>
        <div className="bg-white p-3">
          {isError ? (
            isErrorDiv()
          ) : isLoading ? (
            <div className="text-center">
              <Loading isLoading={isLoading} size={2} />
            </div>
          ) : isInProgress ? (
            <div className="text-center py-5">
              <Alert color="info" isOpen>
                La importación está en {currentStatus.label.toLowerCase()}.
                <br />
                Esta pantalla se actualizará automáticamente cuando termine.
              </Alert>
              <Loading isLoading size={2} />
            </div>
          ) : (
            renderCompletedContent()
          )}
        </div>
      </Container>
    </div>
  )
}

export default LogsImportationRetrieve
